import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Divider,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DayPicker, useNavigation } from "react-day-picker";
import {
  addDays,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns";
import { es } from "date-fns/locale";
import { Calendar, ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import "react-day-picker/dist/style.css";

import { Button } from "./Button";
import { Checkbox } from "./Checkbox";
import { ToggleSwitch } from "./ToggleSwitch";
import { InfoTooltip } from "./InfoTooltip";
import { brand } from "../theme/theme";
import { CustomSelector } from "./CustomSelector";
import { nextRangeOnDayClick } from "../utils/dateUtils";

/**
 * The date bar every metrics panel sits under, modelled on Google Analytics:
 * one range plus the range it is compared against.
 *
 * Both ranges are edited in the same panel and on the same calendar. The field
 * highlighted on the right is the one the calendar is painting; the other range
 * stays visible in the secondary color so the two windows can be lined up at a glance.
 * Nothing leaves the panel until "Aplicar", so a report is fetched once per
 * decision rather than once per click.
 *
 * GA4 counts today as partial, so "yesterday" is the last day worth reporting
 * and every default window ends there.
 */

export const lastCompleteDay = () => subDays(new Date(), 1);

/** A window ending on the last complete day, `subtract` back from it. */
const trailingWindow = (subtract) => () => {
  const to = lastCompleteDay();
  return { from: addDays(subtract(to), 1), to };
};

export const RANGE_PRESETS = [
  {
    value: "today",
    label: "Hoy",
    build: () => ({ from: new Date(), to: new Date() }),
  },
  {
    value: "yesterday",
    label: "Ayer",
    build: () => {
      const yesterday = lastCompleteDay();
      return { from: yesterday, to: yesterday };
    },
  },
  {
    value: "last_week",
    label: "Semana anterior",
    build: () => {
      const reference = subWeeks(new Date(), 1);
      return {
        from: startOfWeek(reference, { weekStartsOn: 1 }),
        to: endOfWeek(reference, { weekStartsOn: 1 }),
      };
    },
  },
  {
    value: "last_month",
    label: "Mes anterior",
    build: () => {
      const reference = subMonths(new Date(), 1);
      return { from: startOfMonth(reference), to: endOfMonth(reference) };
    },
  },
  {
    value: "last_7",
    label: "Últimos 7 días",
    build: () => {
      const to = lastCompleteDay();
      return { from: subDays(to, 6), to };
    },
  },
  {
    value: "last_30",
    label: "Últimos 30 días",
    build: () => {
      const to = lastCompleteDay();
      return { from: subDays(to, 29), to };
    },
  },
  {
    value: "last_3m",
    label: "Últimos 3 meses",
    build: trailingWindow((to) => subMonths(to, 3)),
  },
  {
    value: "last_6m",
    label: "Últimos 6 meses",
    build: trailingWindow((to) => subMonths(to, 6)),
  },
  {
    // On 1 January the last complete day is 31 December, so this is the whole previous year.
    value: "year_to_date",
    label: "Año hasta hoy",
    build: () => {
      const to = lastCompleteDay();
      return { from: startOfYear(to), to };
    },
  },
];

/** Presets of the client's own metrics page: longer windows, each ending on the last complete day. */
export const CLIENT_RANGE_PRESETS = [
  { value: "last_1m", label: "Último mes", build: trailingWindow((to) => subMonths(to, 1)) },
  { value: "last_3m", label: "Últimos 3 meses", build: trailingWindow((to) => subMonths(to, 3)) },
  { value: "last_6m", label: "Últimos 6 meses", build: trailingWindow((to) => subMonths(to, 6)) },
  { value: "last_1y", label: "Último año", build: trailingWindow((to) => subYears(to, 1)) },
];

export const COMPARE_MODES = {
  PREVIOUS: "previous",
  PREVIOUS_YEAR: "previous_year",
  CUSTOM: "custom",
};

const COMPARE_OPTIONS = [
  { value: COMPARE_MODES.PREVIOUS, label: "Periodo anterior" },
  { value: COMPARE_MODES.PREVIOUS_YEAR, label: "Mismo periodo del año anterior" },
  { value: COMPARE_MODES.CUSTOM, label: "Personalizado" },
];

const CUSTOM_PRESET = "custom";

/** The window of the same length immediately before `range`. */
const previousPeriod = (range) => {
  const span = differenceInCalendarDays(range.to, range.from);
  const to = subDays(range.from, 1);
  return { from: subDays(to, span), to };
};

/** The comparison window a non-custom mode implies for the given range. */
export const buildCompareRange = (range, mode) => {
  if (!range?.from || !range?.to) return null;
  if (mode === COMPARE_MODES.PREVIOUS_YEAR) {
    return { from: subYears(range.from, 1), to: subYears(range.to, 1) };
  }
  return previousPeriod(range);
};

export const toIsoDate = (date) => (date ? format(date, "yyyy-MM-dd") : undefined);

/** The state the bar owns, ready for a first render with no user input. */
export const defaultDateSelection = (
  presets = RANGE_PRESETS,
  presetValue = "last_30"
) => {
  const preset = presets.find((item) => item.value === presetValue) || presets[0];
  const range = preset.build();
  return {
    range,
    preset: preset.value,
    compareEnabled: true,
    compareMode: COMPARE_MODES.PREVIOUS,
    compareRange: buildCompareRange(range, COMPARE_MODES.PREVIOUS),
    showPreviousValues: false,
  };
};

const formatRangeLabel = (range) => {
  if (!range?.from || !range?.to) return "Selecciona un rango";
  const pattern = "dd MMM, yyyy";
  return `${format(range.from, pattern, { locale: es })} – ${format(
    range.to,
    pattern,
    { locale: es }
  )}`;
};

/** react-day-picker skin, extended with the secondary-colored "other range" modifiers. */
const calendarSx = (theme, compact) => ({
  "& .rdp": {
    margin: 0,
    "--rdp-cell-size": compact ? "28px" : "32px",
    "--rdp-accent-color": theme.palette.primary.main,
    "--rdp-background-color": alpha(theme.palette.primary.main, 0.12),
  },
  "& .rdp-months": {
    justifyContent: "center",
    gap: compact ? "0.75rem" : "1.25rem",
  },
  "& .rdp-month": { margin: 0 },
  "& .rdp-head_cell": {
    width: compact ? "28px" : "32px",
    fontSize: "0.68rem",
    fontWeight: 600,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  // fixedWeeks pads short months with a 6th row of outside days; with
  // showOutsideDays off those render as an empty, height-less div, so without
  // a fixed cell height that row collapses and the two months drift apart.
  "& .rdp-cell": {
    textAlign: "center",
    padding: 0,
    height: compact ? "26px" : "30px",
  },
  "& .rdp-button_reset, & .rdp-button": {
    width: "100%",
    height: compact ? "26px" : "30px",
    borderRadius: 0,
    fontSize: "0.78rem",
    border: "none",
    boxShadow: "none",
  },
  "& .rdp-button": {
    color: theme.palette.text.primary,
    backgroundColor: "transparent",
  },
  "& .rdp-button[disabled]": { color: theme.palette.text.disabled },

  // The other range, in the secondary brand color, drawn underneath the one being edited.
  "& .rdp-button.day-other": {
    backgroundColor: alpha(brand.orange, 0.22),
  },
  "& .rdp-button.day-other-edge": {
    backgroundColor: alpha(brand.orange, 0.8),
    color: brand.orangeText,
  },
  "& .rdp-button.day-other-start": { borderRadius: "8px 0 0 8px" },
  "& .rdp-button.day-other-end": { borderRadius: "0 8px 8px 0" },

  // The range being edited, in the theme's purple, always on top.
  "& .rdp-button.rdp-day_range_middle": {
    backgroundColor: alpha(theme.palette.primary.main, 0.16),
    borderRadius: 0,
    color: `${theme.palette.text.primary} !important`,
  },
  "& .rdp-button.rdp-day_range_start, & .rdp-button.rdp-day_range_end": {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.primary.contrastText} !important`,
  },
  "& .rdp-button.rdp-day_range_start:not(.rdp-day_range_end)": {
    borderRadius: "8px 0 0 8px",
  },
  "& .rdp-button.rdp-day_range_end:not(.rdp-day_range_start)": {
    borderRadius: "0 8px 8px 0",
  },
  "& .rdp-button.rdp-day_range_start.rdp-day_range_end": { borderRadius: "8px" },

  "& .rdp-button:hover:not([disabled]):not(.rdp-day_range_start):not(.rdp-day_range_end):not(.rdp-day_range_middle)":
    { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
  "& .rdp-button:focus-visible": {
    outline: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
    outlineOffset: "1px",
  },
});

const selectSx = {
  borderRadius: "8px",
  fontSize: "0.8125rem",
  "& .MuiOutlinedInput-input": { padding: "7px 10px" },
};

// The default MenuItem font is much larger than the trigger's; keep the
// opened list at the same size as the collapsed select.
const selectMenuProps = {
  slotProps: {
    paper: {
      sx: { "& .MuiMenuItem-root": { fontSize: "0.8125rem", minHeight: "auto", padding: "8px 12px" } },
    },
  },
};

const presetItemSx = (theme) => ({
  borderRadius: "8px",
  fontSize: "0.8125rem",
  minHeight: "auto",
  padding: "8px 12px",
  "&.Mui-selected, &.Mui-selected:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
});

/** How many years back the calendar's year picker offers, current year included. */
const YEAR_PICKER_SPAN = 6;

/**
 * Calendar caption: the month is plain text (nothing to pick), the year is
 * the app's own minimal selector, and the prev/next arrows are rendered here
 * too, inline, instead of react-day-picker's own Nav.
 *
 * The library's default multi-month nav is CSS-absolute-positioned against
 * each month's own caption box, which in practice drifts away from the
 * calendar it belongs to; folding the arrows into this same flex row keeps
 * them glued to the month they navigate.
 */
function CalendarCaption({ displayMonth }) {
  const { goToMonth, previousMonth, nextMonth, displayMonths } = useNavigation();
  const currentYear = new Date().getFullYear();
  const yearOptions = React.useMemo(
    () =>
      Array.from({ length: YEAR_PICKER_SPAN + 1 }, (_, index) => {
        const year = currentYear - YEAR_PICKER_SPAN + index;
        return { value: String(year), label: String(year) };
      }),
    [currentYear]
  );

  const displayIndex = displayMonths.findIndex((month) =>
    isSameMonth(month, displayMonth)
  );
  const showPrevious = displayIndex === 0;
  const showNext = displayIndex === displayMonths.length - 1;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: "100%", marginBottom: 0.5 }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center">
        {showPrevious && (
          <IconButton
            size="small"
            disabled={!previousMonth}
            onClick={() => previousMonth && goToMonth(previousMonth)}
            sx={{ padding: "4px", color: "text.secondary" }}
          >
            <ChevronLeft size={16} />
          </IconButton>
        )}
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, textTransform: "capitalize" }}
        >
          {format(displayMonth, "LLLL", { locale: es })}
        </Typography>
        <CustomSelector
          value={String(displayMonth.getFullYear())}
          onChange={(year) =>
            goToMonth(new Date(Number(year), displayMonth.getMonth(), 1))
          }
          options={yearOptions}
          size="sm"
          searchable={false}
          triggerVariant="minimal"
          menuWidth={90}
        />
      </Stack>

      {showNext && (
        <IconButton
          size="small"
          disabled={!nextMonth}
          onClick={() => nextMonth && goToMonth(nextMonth)}
          sx={{ padding: "4px", color: "text.secondary" }}
        >
          <ChevronRight size={16} />
        </IconButton>
      )}
    </Stack>
  );
}

CalendarCaption.propTypes = {
  displayMonth: PropTypes.instanceOf(Date).isRequired,
};

/** The pill trigger: an icon, the range in words and a coloured left edge. */
function RangePill({
  icon: Icon,
  accent,
  label,
  onClick,
  disabled,
  active,
  className,
  dense,
}) {
  const theme = useTheme();

  return (
    <Stack
      component="button"
      type="button"
      className={className}
      direction="row"
      spacing={dense ? 0.75 : 1}
      alignItems="center"
      onClick={onClick}
      disabled={disabled}
      sx={{
        border: `1px solid ${active ? accent : theme.palette.divider}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: "8px",
        backgroundColor: theme.palette.background.paper,
        boxShadow: active ? `0 0 0 2px ${alpha(accent, 0.2)}` : "none",
        padding: dense ? "6px 10px" : "8px 12px",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        color: theme.palette.text.primary,
        font: "inherit",
        transition: "background-color 0.15s ease",
        "&:hover": {
          backgroundColor: disabled ? undefined : theme.palette.action.hover,
        },
      }}
    >
      <Icon size={dense ? 14 : 16} color={accent} />
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          whiteSpace: "nowrap",
          fontSize: dense ? "0.8125rem" : undefined,
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

/** Form-field trigger, like CustomSelector's, for the `presetList` mode used inside forms. */
function RangeField({ label, onClick, disabled, active }) {
  const theme = useTheme();

  return (
    <Stack
      component="button"
      type="button"
      direction="row"
      spacing={1}
      alignItems="center"
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: "100%",
        height: 36,
        padding: "8px 12px",
        border: `1px solid ${
          active ? theme.palette.primary.main : theme.palette.border?.input || theme.palette.divider
        }`,
        borderRadius: "8px",
        backgroundColor: disabled ? theme.palette.action.disabledBackground : theme.palette.background.default,
        color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
        font: "inherit",
        fontSize: "0.875rem",
        textAlign: "left",
        cursor: disabled ? "not-allowed" : "pointer",
        "&:hover": {
          borderColor: disabled || active ? undefined : theme.palette.border?.default || theme.palette.divider,
        },
      }}
    >
      <Calendar size={16} color={theme.palette.text.secondary} />
      <Box component="span" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </Box>
      <ChevronDown
        size={16}
        style={{ transform: active ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }}
      />
    </Stack>
  );
}

RangeField.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  active: PropTypes.bool,
};

RangePill.propTypes = {
  icon: PropTypes.elementType.isRequired,
  accent: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  active: PropTypes.bool,
  className: PropTypes.string,
  dense: PropTypes.bool,
};

/**
 * Range + comparison selector.
 *
 * `value` is the whole selection and only changes on "Aplicar":
 * `{ range, preset, compareEnabled, compareMode, compareRange, showPreviousValues }`.
 * `presetList` drops the comparison and lists the presets beside the calendar, with `customRangeHint` as a tooltip on "Personalizado".
 */
export function MetricsDateRangeBar({
  value,
  onChange,
  disabled = false,
  presets = RANGE_PRESETS,
  presetList = false,
  customRangeHint,
  overlayZIndex,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isShortViewport = useMediaQuery("(max-height: 860px)");
  const compact = isMobile || isShortViewport;

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [draft, setDraft] = React.useState(value);
  const [activeField, setActiveField] = React.useState("range");

  const isOpen = Boolean(anchorEl);
  const compareAccent = brand.orange;
  const menuProps = { ...selectMenuProps, sx: { zIndex: overlayZIndex } };

  // The panel opens on the whole bar so it does not jump between the two pills.
  const open = (event, field) => {
    setDraft(value);
    setActiveField(field);
    setAnchorEl(event.currentTarget.closest("[data-date-bar]"));
  };

  const patch = (changes) => setDraft((current) => ({ ...current, ...changes }));

  /** Picking a preset rebuilds the range, and the comparison that follows it. */
  const handlePresetChange = (presetValue) => {
    if (presetValue === CUSTOM_PRESET) {
      patch({ preset: CUSTOM_PRESET });
      setActiveField("range");
      return;
    }
    const range = presets.find((p) => p.value === presetValue).build();
    patch({
      preset: presetValue,
      range,
      compareRange:
        draft.compareMode === COMPARE_MODES.CUSTOM
          ? draft.compareRange
          : buildCompareRange(range, draft.compareMode),
    });
  };

  const handleCompareModeChange = (mode) => {
    patch({
      compareMode: mode,
      compareRange:
        mode === COMPARE_MODES.CUSTOM
          ? draft.compareRange
          : buildCompareRange(draft.range, mode),
    });
    setActiveField(mode === COMPARE_MODES.CUSTOM ? "compare" : "range");
  };

  /**
   * One calendar, two targets: whichever field is active receives the click,
   * and painting by hand turns that field into "Personalizado".
   */
  const handleDayClick = (_selected, day) => {
    const range = nextRangeOnDayClick(
      activeField === "compare" ? draft.compareRange : draft.range,
      day
    );

    if (activeField === "compare") {
      patch({ compareMode: COMPARE_MODES.CUSTOM, compareRange: range });
      return;
    }

    patch({
      preset: CUSTOM_PRESET,
      range,
      compareRange:
        draft.compareMode === COMPARE_MODES.CUSTOM
          ? draft.compareRange
          : buildCompareRange(range, draft.compareMode),
    });
  };

  const handleApply = () => {
    onChange(draft);
    setAnchorEl(null);
  };

  const editedRange = activeField === "compare" ? draft.compareRange : draft.range;
  const otherRange = activeField === "compare" ? draft.range : draft.compareRange;
  const showOther = Boolean(
    draft.compareEnabled && otherRange?.from && otherRange?.to
  );

  const isApplyDisabled =
    !draft.range?.from ||
    !draft.range?.to ||
    (draft.compareEnabled &&
      (!draft.compareRange?.from || !draft.compareRange?.to));

  const calendar = (
    <Box sx={calendarSx(theme, compact)}>
      <DayPicker
        mode="range"
        locale={es}
        selected={editedRange || undefined}
        onSelect={handleDayClick}
        numberOfMonths={isMobile ? 1 : 2}
        defaultMonth={editedRange?.from || new Date()}
        components={{ Caption: CalendarCaption }}
        disabled={{ after: new Date() }}
        modifiers={
          showOther
            ? {
                other: otherRange,
                otherStart: otherRange.from,
                otherEnd: otherRange.to,
              }
            : {}
        }
        modifiersClassNames={{
          other: "day-other",
          otherStart: "day-other-edge day-other-start",
          otherEnd: "day-other-edge day-other-end",
        }}
        fixedWeeks
      />
    </Box>
  );

  const actions = (
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      <Button variant="outline" onClick={() => setAnchorEl(null)}>
        Cancelar
      </Button>
      <Button onClick={handleApply} disabled={isApplyDisabled}>
        Aplicar
      </Button>
    </Stack>
  );

  return (
    <>
      {presetList ? (
        <Box data-date-bar>
          <RangeField
            label={formatRangeLabel(value.range)}
            disabled={disabled}
            active={isOpen}
            onClick={(event) => open(event, "range")}
          />
        </Box>
      ) : (
      <Stack
        data-date-bar
        direction="row"
        spacing={1.5}
        flexWrap="wrap"
        useFlexGap
        sx={{
          width: "fit-content",
          // Both pills open the very same panel, so hovering either previews
          // the hover state on both to make that pairing obvious.
          ...(!disabled && {
            "&:hover .date-range-pill": {
              backgroundColor: theme.palette.action.hover,
            },
          }),
        }}
      >
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Rango de fechas:
          </Typography>
          <RangePill
            className="date-range-pill"
            icon={Calendar}
            accent={theme.palette.primary.main}
            label={formatRangeLabel(value.range)}
            disabled={disabled}
            active={isOpen && activeField === "range"}
            onClick={(event) => open(event, "range")}
          />
        </Stack>

        {value.compareEnabled && (
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              Comparar con:
            </Typography>
            <RangePill
              className="date-range-pill"
              icon={ArrowUpDown}
              accent={compareAccent}
              label={formatRangeLabel(value.compareRange)}
              disabled={disabled}
              active={isOpen && activeField === "compare"}
              onClick={(event) => open(event, "compare")}
            />
          </Stack>
        )}
      </Stack>
      )}

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{ zIndex: overlayZIndex }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              padding: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px",
              boxShadow: theme.shadows[6],
              maxWidth: "calc(100vw - 24px)",
            },
          },
        }}
      >
        {presetList ? (
          <Stack direction={isMobile ? "column" : "row"} spacing={2}>
            <Stack spacing={0.25} sx={{ minWidth: 170 }}>
              {[...presets, { value: CUSTOM_PRESET, label: "Personalizado" }].map((preset) => (
                <MenuItem
                  key={preset.value}
                  selected={draft.preset === preset.value}
                  onClick={() => handlePresetChange(preset.value)}
                  sx={{ ...presetItemSx(theme), gap: 1 }}
                >
                  {preset.label}
                  {preset.value === CUSTOM_PRESET && customRangeHint && (
                    <InfoTooltip
                      title={customRangeHint}
                      size={14}
                      zIndex={overlayZIndex && overlayZIndex + 1}
                    />
                  )}
                </MenuItem>
              ))}
            </Stack>
            <Divider orientation={isMobile ? "horizontal" : "vertical"} flexItem />
            <Stack spacing={1.5}>
              {calendar}
              <Divider />
              {actions}
            </Stack>
          </Stack>
        ) : (
          <Stack direction={isMobile ? "column" : "row"} spacing={2}>
            {calendar}

            <Stack spacing={1} sx={{ minWidth: isMobile ? "auto" : 250 }}>
              <Stack spacing={0.75}>
                <Typography variant="caption" color="text.secondary">
                  Rango de fechas:
                </Typography>
                <Select
                  size="small"
                  value={draft.preset}
                  onChange={(event) => handlePresetChange(event.target.value)}
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  {presets.map((preset) => (
                    <MenuItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </MenuItem>
                  ))}
                  <MenuItem value={CUSTOM_PRESET}>Personalizado</MenuItem>
                </Select>
                <RangePill
                  dense
                  icon={Calendar}
                  accent={theme.palette.primary.main}
                  label={formatRangeLabel(draft.range)}
                  active={activeField === "range"}
                  onClick={() => setActiveField("range")}
                />
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <ToggleSwitch
                  checked={draft.compareEnabled}
                  onChange={(event) =>
                    patch({ compareEnabled: event.target.checked })
                  }
                />
                <Typography variant="caption" color="text.secondary">
                  Comparar con:
                </Typography>
              </Stack>

              <Stack spacing={0.75} sx={{ opacity: draft.compareEnabled ? 1 : 0.5 }}>
                <Select
                  size="small"
                  value={draft.compareMode}
                  disabled={!draft.compareEnabled}
                  onChange={(event) => handleCompareModeChange(event.target.value)}
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  {COMPARE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <RangePill
                  dense
                  icon={ArrowUpDown}
                  accent={compareAccent}
                  label={formatRangeLabel(draft.compareRange)}
                  disabled={!draft.compareEnabled}
                  active={activeField === "compare"}
                  onClick={() => {
                    setActiveField("compare");
                    if (draft.compareMode !== COMPARE_MODES.CUSTOM) {
                      patch({ compareMode: COMPARE_MODES.CUSTOM });
                    }
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={0.5} alignItems="center">
                <Checkbox
                  id="show-previous-values"
                  checked={draft.showPreviousValues}
                  disabled={!draft.compareEnabled}
                  onCheckedChange={(checked) => patch({ showPreviousValues: checked })}
                />
                <Typography
                  component="label"
                  htmlFor="show-previous-values"
                  variant="body2"
                  sx={{ cursor: "pointer" }}
                >
                  Mostrar valores previos
                </Typography>
                <InfoTooltip
                  title="Añade el valor del periodo de comparación junto a cada métrica."
                  size={14}
                />
              </Stack>

              <Divider />

              {actions}
            </Stack>
          </Stack>
        )}
      </Popover>
    </>
  );
}

const rangeShape = PropTypes.shape({
  from: PropTypes.instanceOf(Date),
  to: PropTypes.instanceOf(Date),
});

MetricsDateRangeBar.propTypes = {
  value: PropTypes.shape({
    range: rangeShape,
    preset: PropTypes.string,
    compareEnabled: PropTypes.bool,
    compareMode: PropTypes.string,
    compareRange: rangeShape,
    showPreviousValues: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  presets: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      build: PropTypes.func.isRequired,
    })
  ),
  presetList: PropTypes.bool,
  customRangeHint: PropTypes.string,
  overlayZIndex: PropTypes.number,
};

export default MetricsDateRangeBar;
